import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'

const MIN_PASSWORD_LENGTH = 8

const COPY = {
  reset: {
    title: 'Set new password',
    subtitle: (email) => `Choose a new password for ${email}`,
    expired: 'This password reset link is invalid or has expired. Please request a new one.',
    submit: 'Update password',
  },
  invite: {
    title: 'Welcome to Backstube Accounting',
    subtitle: (email) => `Choose a password for ${email} to finish setting up your account`,
    expired: 'This invite link is invalid or has expired. Ask your administrator to send a new invite.',
    submit: 'Set password',
  },
}

const ResetPassword = () => {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const { user, passwordFlow, updatePassword, clearPasswordFlow } = useAuth()
  const copy = COPY[passwordFlow] ?? COPY.reset

  const finishRecovery = () => {
    clearPasswordFlow()
    // Remove the email-link path and any tokens from the URL
    window.history.replaceState(null, '', '/')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`)
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      const result = await updatePassword(password)

      if (result.success) {
        finishRecovery()
      } else {
        setError(result.error?.message || 'Failed to update password')
      }
    } catch (err) {
      setError('An unexpected error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  // No session means the recovery link is invalid or has expired
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-4">
        <div className="w-full max-w-sm text-center">
          <h2 className="text-lg font-semibold text-black mb-2">Link expired</h2>
          <p className="text-sm text-gray-500 mb-6">
            {copy.expired}
          </p>

          <button
            type="button"
            onClick={finishRecovery}
            className="w-full py-2.5 bg-black text-white text-sm font-medium rounded-md hover:bg-gray-900 transition-colors"
          >
            Back to sign in
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h2 className="text-lg font-semibold text-black">{copy.title}</h2>
          <p className="text-sm text-gray-400 mt-1">
            {copy.subtitle(user.email)}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="new-password" className="block text-xs font-medium text-gray-500 mb-1.5">
              New password
            </label>
            <input
              type="password"
              id="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
              disabled={isLoading}
              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm text-black placeholder-gray-300 focus:outline-none focus:border-black transition-colors disabled:bg-gray-50 disabled:text-gray-400"
            />
          </div>

          <div>
            <label htmlFor="confirm-password" className="block text-xs font-medium text-gray-500 mb-1.5">
              Confirm password
            </label>
            <input
              type="password"
              id="confirm-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
              disabled={isLoading}
              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm text-black placeholder-gray-300 focus:outline-none focus:border-black transition-colors disabled:bg-gray-50 disabled:text-gray-400"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-2.5 bg-black text-white text-sm font-medium rounded-md hover:bg-gray-900 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Saving...' : copy.submit}
          </button>
        </form>
      </div>
    </div>
  )
}

export default ResetPassword
